import React, {Component} from 'react';
import ReactTable from 'react-table';
import {withTranslation} from 'react-i18next';
import {reactTableText} from '../../constants/configs';
import ReactTableSelectFilter from "../../components/ReactTableSelectFilter";
import {NotificationManager} from "../../components/common/react-notifications";
import {getStatusColor, i18nizeDate, mapStatus, numberWithCommas} from "../../helpers/Utils";
import ModalManager from "../../components/modal/ModalManager";
import ZarinpalTransferForm from "../ZarinpalTransferForm";
import {keyBy, mapValues, omit} from "lodash";
import Rbac from "../../components/Rbac";
import Api from "../../services/Api";
import {Button} from "reactstrap";

class JobRequests extends Component {
  constructor(props) {
    super(props);
    this.state = {
      data: [],
      loading: true,
      pages: -1,
    };
    this.renderStatusFilter = this.renderStatusFilter.bind(this);
  }

  fetchData = async state => {
    const {userId} = this.props;
    try {
      const offset = state.page * state.pageSize;
      const {data} = await Api.invoke(
        'GET',
        '/v2/admin-ui/transfers/jobs',
        null,
        {
          params: {
            userId,
            offset,
            limit: state.pageSize,
            ...(state.filtered.length ? omit(mapValues(keyBy(state.filtered, 'id'), 'value'), 'timestamp') : null),
          }
        },
      );
      this.setState({
        data: data.rows,
        pages: Math.ceil(data.count / state.pageSize),
      });
    } catch (e) {
      console.log('error: ', e);
    } finally {
      this.setState({
        loading: false,
      });
    }
  };

  cancelRequest = async jobId => {
    const {t, userId} = this.props;
    try {
      await Api.invoke(
        'PATCH',
        `/v2/admin-ui/transfers/jobs/${jobId}`,
        {
          userId,
          status: 'CANCELED',
        },
      );
      NotificationManager.success(t('Cancel request sent successfully'), t('Message'));
      this.table.fireFetchData();
    } catch (e) {
      console.log('error: ', e);
    }
  };

  onPressCancel = jobId => {
    const {t} = this.props;
    ModalManager.open({
      isOpen: true,
      title: t('Confirm Cancel'),
      body: t('Are you sure you want to cancel this request ?'),
      buttons: [
        {
          color: 'primary',
          onClick: async () => {
            this.cancelRequest(jobId);
            ModalManager.close({
              isOpen: false
            });
          },
          text: t('Accept'),
        },
        {
          color: 'grey',
          onClick: () => {
            ModalManager.close({
              isOpen: false
            });
          },
          text: t('Close'),
        },
      ],
    });
  };

  getTransferPolice = (policy, token) => {
    const {t} = this.props;
    const {type, quantity, amount, maxAmountValue} = policy;
    if (type === "ONCE") {
      if (quantity === "FIX_VALUE") {
        return t('Transfer amount of {{amount}} {{token}}', {
          amount: numberWithCommas(Number(amount)),
          token,
        });
      }
      if (quantity === "PERCENTAGE") {
        if (maxAmountValue) {
          return t('Transfer {{amount}}% of {{token}} balance up to {{maxAmountValue}} {{token}}', {
            amount: numberWithCommas(Number(amount)),
            maxAmountValue: numberWithCommas(Number(maxAmountValue)),
            token,
          });
        }
        return t('Transfer {{amount}}% of {{token}} balance', {
          amount: numberWithCommas(Number(amount)),
          token,
        });
      }
    }
    if (type === "DAILY") {
      if (quantity === "FIX_VALUE") {
        return t('Transfer amount of {{amount}} {{token}} with daily period', {
          amount: numberWithCommas(Number(amount)),
          token,
        });
      }
      if (quantity === "PERCENTAGE") {
        if (maxAmountValue) {
          return t('Transfer {{amount}}% of {{token}} balance up to {{maxAmountValue}} {{token}} with daily period', {
            amount: numberWithCommas(Number(amount)),
            maxAmountValue: numberWithCommas(Number(maxAmountValue)),
            token,
          });
        }
        return t('Transfer {{amount}}% of {{token}} balance with daily period', {
          amount: numberWithCommas(Number(amount)),
          token,
        });
      }
    }
  };

  getColumns = t => [
    {
      Header: t('Transfer policy'),
      accessor: 'transferPolice',
      Cell: props => {
        return (
          <span className="font-weight-semibold">
            {this.getTransferPolice(props.value, t(props.original.token))}
          </span>
        );
      },
      minWidth: 300,
    },
    {
      Header: t('Gateway'),
      accessor: 'sourceInfo.terminal.name',
    },
    {
      Header: t('Fee'),
      accessor: 'feePolice',
      Cell: props => {
        const {token} = props.original;
        const {quantity, amount} = props.value;
        if (quantity === "FIX_VALUE") {
          return <span>{`${numberWithCommas(Number(amount))} ${t(token)}`}</span>
        }
        return (
          <span>{`${numberWithCommas(Number(amount))}%`}</span>
        );
      },
      width: 200,
    },
    {
      Header: t('Status'),
      accessor: 'status',
      filterable: true,
      Filter: this.renderStatusFilter,
      Cell: props => <span className={getStatusColor(props.value)}>{mapStatus(props.value)}</span>,
      width: 200,
    },
    {
      Header: t('Submit Date'),
      accessor: 'createdAt',
      Cell: props => <span>{i18nizeDate(props.value)}</span>,
      width: 200,
    },
    {
      Header: t('Expiration date'),
      accessor: 'expiredAt',
      Cell: props => <span>{i18nizeDate(props.value)}</span>,
      width: 200,
    },
    {
      Header: t('Actions'),
      accessor: 'id',
      Cell: props => {
        const {status} = props.original;
        if (status === 'READY' || status === 'STARTED') {
          return (
            <div
              className="text-danger cursor-pointer"
              onClick={() => {this.onPressCancel(props.value);}}>
              <i className="simple-icon-close iconsminds-close"/>
              <span className="mx-1">{t('Cancel request')}</span>
            </div>
          );
        }
        return null;
      },
      width: 150,
      show: Rbac.hasPermission('update_transfer-wallet-balance'),
    },
  ];

  renderStatusFilter({filter, onChange}) {
    const {t} = this.props;
    const options = [
      {value: '', label: t('all')},
      {value: 'READY', label: t('Ready')},
      {value: 'STARTED', label: t('Started')},
      {value: 'FINISHED', label: t('Finished')},
      {value: 'EXPIRED', label: t('Expired')},
      {value: 'CANCELED', label: t('Canceled')},
      {value: 'FAILED', label: t('Failed')},
    ];
    return <ReactTableSelectFilter
      filter={filter}
      onChange={onChange}
      options={options}
    />;
  }

  onPressAdd = () => {
    const {t} = this.props;
    ModalManager.open({
      isOpen: true,
      title: t('Submit Transfer Request'),
      body: this.renderZarinpalTransferForm(),
    });
  };

  renderZarinpalTransferForm = () => {
    const {userId, fetchBalances, balances} = this.props;
    return (
      <ZarinpalTransferForm
        userId={userId}
        balances={balances}
        afterSubmit={() => {
          fetchBalances();
          this.table.fireFetchData();
        }}
      />
    );
  };

  render() {
    const {t} = this.props;
    const {data, loading, pages} = this.state;
    return (
      <div style={{marginTop: 20, marginBottom: 10}}>
        <Rbac permission='create_transfer-wallet-balance'>
          <div className="d-flex align-items-center justify-content-end mb-3">
            <Button onClick={this.onPressAdd}>
              <i className="simple-icon-plus add-reply-icon"/>
              <span className="mx-1">{t('Submit Request')}</span>
            </Button>
          </div>
        </Rbac>
        <ReactTable
          manual
          data={data}
          pages={pages}
          loading={loading}
          onFetchData={this.fetchData}
          columns={this.getColumns(t)}
          ref={el => {this.table = el;}}
          className="-highlight"
          defaultPageSize={10}
          sortable={false}
          resizable={true}
          style={{
            width: '100%',
          }}
          {...reactTableText}
        />
      </div>
    );
  }
}

export default withTranslation()(JobRequests);
